import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64) {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(b64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
}

export default function NotificationsCard() {
    const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
    const [supported, setSupported] = useState(false);
    const [subscribed, setSubscribed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [vapid, setVapid] = useState("");

    useEffect(() => {
        const ok = "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
        setSupported(ok);
        if (!ok) return;

        api.get("/push/vapid").then((r) => setVapid(r.data.public_key)).catch(() => {});
        navigator.serviceWorker.ready.then(async (reg) => {
            const sub = await reg.pushManager.getSubscription();
            setSubscribed(!!sub);
        }).catch(() => {});
    }, []);

    const subscribe = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const perm = await Notification.requestPermission();
            setPermission(perm);
            if (perm !== "granted") {
                toast.error("Notifications refusées. Autorisez-les dans les réglages du navigateur.");
                return;
            }
            const reg = await navigator.serviceWorker.ready;
            let sub = await reg.pushManager.getSubscription();
            if (!sub) {
                if (!vapid) {
                    toast.error("Clé serveur indisponible. Réessayez dans un instant.");
                    return;
                }
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapid),
                });
            }
            const json = sub.toJSON();
            await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
            setSubscribed(true);
            toast.success("Notifications activées");
        } catch (e) {
            console.error("Push subscribe failed", e);
            toast.error("Impossible d'activer les notifications");
        } finally {
            setBusy(false);
        }
    };

    const unsubscribe = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
                await sub.unsubscribe();
            }
            setSubscribed(false);
            toast.success("Notifications désactivées");
        } catch (e) {
            console.error(e);
            toast.error("Erreur");
        } finally {
            setBusy(false);
        }
    };

    const sendTest = async () => {
        try {
            await api.post("/push/test");
            toast.success("Notification test envoyée");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    if (!supported) {
        return (
            <div className="glass rounded-2xl p-4 mb-5 flex items-center gap-3 opacity-60" data-testid="notif-unsupported">
                <BellOff className="w-5 h-5" />
                <p className="text-sm font-body">Notifications non supportées sur ce navigateur</p>
            </div>
        );
    }

    return (
        <div className="glass rounded-2xl p-4 mb-5 flex items-center gap-3" data-testid="notif-card">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${subscribed ? "bg-[#25D366]/20 text-[#25D366]" : "bg-white/5 text-white/70"}`}>
                {subscribed ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-sm">Notifications de commandes</p>
                <p className="text-[11px] text-white/60 font-body">
                    {subscribed
                        ? "Vous serez alerté sur cet appareil à chaque nouvelle commande WhatsApp."
                        : "Recevez une alerte instantanée à chaque commande, même app fermée."}
                </p>
            </div>
            {subscribed ? (
                <div className="flex gap-1 shrink-0">
                    <button
                        data-testid="notif-test-btn"
                        onClick={sendTest}
                        disabled={busy}
                        className="h-9 px-3 rounded-full bg-white/10 border border-white/20 text-xs font-body font-semibold"
                    >
                        Test
                    </button>
                    <button
                        data-testid="notif-unsub-btn"
                        onClick={unsubscribe}
                        disabled={busy}
                        className="h-9 px-3 rounded-full bg-red-500/20 border border-red-500/30 text-red-400 text-xs font-body font-semibold"
                    >
                        Couper
                    </button>
                </div>
            ) : (
                <button
                    data-testid="notif-sub-btn"
                    onClick={subscribe}
                    disabled={busy}
                    className="h-10 px-4 orange-btn rounded-full text-white text-xs font-body font-bold flex items-center gap-1 shrink-0"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activer"}
                </button>
            )}
        </div>
    );
}
